import { BUSINESS_PATH } from '@parameters/routing/business.parameter';
import { PORTAL } from '@parameters/routing/portal.parameter';
import {
  resolveEntityPortalLandingUrl,
  resolveEntityPortalLandingUrlExcluding,
} from './entity-permissions.helper';
import {
  MerchantPortalNavigationOptions,
  resolveMerchantPortalLandingUrl,
  resolveMerchantPortalLandingUrlExcluding,
} from './merchant-permissions.helper';

/**
 * First portal page the signed-in user can open for the current portal type.
 * Empty permission lists keep the role default (dashboard).
 */
export function resolvePortalHomeUrl(
  portalType: PORTAL | string | undefined | null,
  entityPermissions: string[] = [],
  merchantPermissions: string[] = [],
  merchantNavigation?: MerchantPortalNavigationOptions
): string {
  if (!portalType) {
    return '/';
  }

  const preferred = `/${BUSINESS_PATH.portal}/${portalType}/dashboard/main`;

  if (portalType === PORTAL.merchant) {
    return resolveMerchantPortalLandingUrl(
      preferred,
      merchantPermissions,
      merchantNavigation
    );
  }

  return resolveEntityPortalLandingUrl(preferred, entityPermissions);
}

/**
 * Home URL when leaving a sub-area (Developers / Payments).
 * Uses the normal preferred home when it is already outside that area;
 * otherwise the next accessible page outside `excludePathSegment`.
 */
export function resolvePortalExitUrl(
  portalType: PORTAL | string | undefined | null,
  entityPermissions: string[] = [],
  merchantPermissions: string[] = [],
  excludePathSegment?: string,
  merchantNavigation?: MerchantPortalNavigationOptions
): string {
  const home = resolvePortalHomeUrl(
    portalType,
    entityPermissions,
    merchantPermissions,
    merchantNavigation
  );

  if (!excludePathSegment || !home.includes(excludePathSegment)) {
    return home;
  }

  if (!portalType) {
    return home;
  }

  const alternative =
    portalType === PORTAL.merchant
      ? resolveMerchantPortalLandingUrlExcluding(
          merchantPermissions,
          excludePathSegment,
          merchantNavigation
        )
      : resolveEntityPortalLandingUrlExcluding(
          entityPermissions,
          excludePathSegment
        );

  return alternative ?? home;
}

/**
 * True when the user can open at least one portal home outside
 * `excludePathSegment` (e.g. show Back while on Developers).
 */
export function hasPortalHomeOutside(
  portalType: PORTAL | string | undefined | null,
  entityPermissions: string[] = [],
  merchantPermissions: string[] = [],
  excludePathSegment: string,
  merchantNavigation?: MerchantPortalNavigationOptions
): boolean {
  if (!excludePathSegment) {
    return true;
  }

  const exitUrl = resolvePortalExitUrl(
    portalType,
    entityPermissions,
    merchantPermissions,
    excludePathSegment,
    merchantNavigation
  );

  return !exitUrl.includes(excludePathSegment);
}
